import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";

import { AimaLogo } from "@/components/AimaLogo";
import { LanguageSelector } from "@/components/LanguageSelector";
import { CinematicVignette } from "@/components/CinematicVignette";
import { AmbientBackground } from "@/components/AmbientBackground";
import { Footer } from "@/pages/Landing";
import { getLegalPage, LEGAL_LAST_UPDATED } from "@/data/legal";

const LegalPage = ({ slug }) => {
    const { t, i18n } = useTranslation();
    const { lang } = useParams();
    const activeLang = lang || i18n.language || "en";
    const data = getLegalPage(activeLang, slug);

    useEffect(() => {
        if (!data) return;
        document.title = data.metaTitle;
        const desc = document.querySelector('meta[name="description"]');
        if (desc) desc.setAttribute("content", data.metaDescription);
        window.scrollTo(0, 0);
    }, [data, slug, activeLang]);

    if (!data) return null;

    return (
        <div className="relative min-h-screen bg-black text-white overflow-x-hidden">
            <CinematicVignette />

            {/* Top bar */}
            <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/65 border-b border-white/[0.06]">
                <div className="container-x max-w-[1000px] mx-auto flex items-center justify-between h-16 md:h-20 gap-4">
                    <Link to={`/${activeLang}`} data-testid="legal-logo-link" className="flex items-center shrink-0">
                        <AimaLogo variant="horizontal" height={28} />
                    </Link>
                    <div className="flex items-center gap-4">
                        <Link
                            to={`/${activeLang}`}
                            data-testid="legal-back-link"
                            className="group inline-flex items-center gap-2 font-plex text-[13px] text-white/55 hover:text-white transition-colors"
                        >
                            <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" strokeWidth={1.7} />
                            {t("legal.back")}
                        </Link>
                        <LanguageSelector />
                    </div>
                </div>
            </header>

            <main className="relative">
                <AmbientBackground variant="grid" opacity={0.5} />
                <div className="relative container-x max-w-[1000px] mx-auto py-16 md:py-24">
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <div className="eyebrow mb-5">{t("legal.eyebrow")}</div>
                        <h1 className="h-display text-4xl md:text-5xl lg:text-[64px] text-white leading-[0.95]" data-testid="legal-title">
                            {data.title}
                        </h1>
                        <div className="mt-5 font-mono text-[10.5px] uppercase tracking-[0.22em] text-white/40">
                            {t("legal.last_updated")}: {LEGAL_LAST_UPDATED[activeLang] || LEGAL_LAST_UPDATED.en}
                        </div>
                    </motion.div>

                    <div className="mt-12 space-y-4">
                        {data.intro.map((p, i) => (
                            <p key={`intro-${i}`} className="font-plex text-[15.5px] md:text-base text-white/70 leading-[1.85] max-w-[68ch]">
                                {p}
                            </p>
                        ))}
                    </div>

                    <div className="mt-12 space-y-12">
                        {data.sections.map((s, si) => (
                            <section key={si} className="border-t border-white/[0.06] pt-8">
                                <h2 className="font-cabinet text-white text-xl md:text-2xl tracking-tight mb-4">
                                    {s.h}
                                </h2>
                                <div className="space-y-4">
                                    {(s.p || []).map((p, pi) => (
                                        <p key={pi} className="font-plex text-[15px] text-white/65 leading-[1.85] max-w-[68ch]">
                                            {p}
                                        </p>
                                    ))}
                                    {s.list && (
                                        <ul className="space-y-2.5 pt-1">
                                            {s.list.map((li, li2) => (
                                                <li key={li2} className="flex gap-3 font-plex text-[15px] text-white/65 leading-[1.7] max-w-[66ch]">
                                                    <span className="text-cyan mt-[3px] font-mono shrink-0">·</span>
                                                    <span>{li}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    {(s.pAfter || []).map((p, pi) => (
                                        <p key={`after-${pi}`} className="font-plex text-[15px] text-white/65 leading-[1.85] max-w-[68ch] pt-1">
                                            {p}
                                        </p>
                                    ))}
                                </div>
                            </section>
                        ))}
                    </div>

                    <div className="mt-16 border-t border-white/[0.06] pt-8">
                        <Link
                            to={`/${activeLang}`}
                            data-testid="legal-back-bottom"
                            className="group inline-flex items-center gap-3 h-12 px-6 border border-white/15 hover:border-white/40 text-white font-plex text-sm transition-colors"
                        >
                            <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" strokeWidth={1.7} />
                            {t("legal.back_platform")}
                        </Link>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default LegalPage;
